import React from "react";
import get from "lodash/get";
import { object, array, bool, string, number } from "prop-types";
import assetify from "@quintype/framework/assetify";
import cx from "classnames";
import { ResponsiveImage, WithLazy } from "@quintype/components";

import Icon from "../../icons";
import DefaultCardImage from "./default-card-image.svg";
import VideoBlackIcon from "../icons/VideoBlackIcon";
import GalleryBlackIcon from "../icons/GalleryBlackIcon";

import "./cardImage.m.css";

const CardImage = ({
  story = {},
  aspectRatio = [1, 1],
  defaultWidth = 500,
  imageType = "image1x1",
  zoom = true,
  widths = [250, 480, 764],
  eager = true,
  showStoryTypeIcon = true,
  showAlternateImage = false,
  darkTheme = false,
  largeIcon = false,
  isCommentedStory = false,
}) => {
  const wrapperStyleName = cx(
    "qt-image-dynamic",
    { image16x9: imageType === "image16x9" },
    { image1x1: imageType === "image1x1" },
    { image2x1: imageType === "image2x1" },
    { image3x4: imageType === "image3x4" },
    { image3x2: imageType === "image3x2" },
    { image2x3: imageType === "image2x3" },
    { image1200x500: imageType === "image1200x500" }
  );

  let heroImage = "";
  let heroImageMetaData = "";
  if (showAlternateImage && get(story, ["alternative", "home", "default", "hero-image"])) {
    heroImage = story.alternative.home.default["hero-image"]["hero-image-s3-key"]
      ? story.alternative.home.default["hero-image"]["hero-image-s3-key"]
      : story.alternative.home.default["hero-image"].key
      ? story.alternative.home.default["hero-image"].key
      : "";
    heroImageMetaData = story.alternative.home.default["hero-image"]["hero-image-metadata"]
      ? story.alternative.home.default["hero-image"]["hero-image-metadata"]
      : story.alternative.home.default["hero-image"].metadata
      ? story.alternative.home.default["hero-image"].metadata
      : "";
  } else if (story["hero-image-s3-key"]) {
    heroImage = story["hero-image-s3-key"];
    heroImageMetaData = story["hero-image-metadata"];
  }

  if (typeof window === "undefined") {
    return (
      <figure>
        <picture className="qt-image">
          <img
            className="picture"
            src={`${assetify(DefaultCardImage)}`}
            alt={story["hero-image-caption"] || story.headline}
          />
        </picture>
      </figure>
    );
  }

  const MostCommentedStoryImage = () =>
    story["hero-image-s3-key"] ? (
      <img src={story["hero-image-s3-key"]} alt={story.headline} className="qt-image" />
    ) : (
      <img
        className="picture"
        src={`${assetify(DefaultCardImage)}`}
        alt={story["hero-image-caption"] || story.headline}
        styleName="default-image"
      />
    );

  return (
    <div styleName={wrapperStyleName + " card-image-wrapper"} className={wrapperStyleName + " card-image-wrapper"}>
      <figure className="qt-figure">
        <picture
          className={`qt-image ${zoom ? "zoom-desktop" : ""} ${
            showAlternateImage || (isCommentedStory && story["hero-image-s3-key"]) ? "default" : ""
          }`}
        >
          <WithLazy margin="50px">
            {() =>
              isCommentedStory ? (
                <MostCommentedStoryImage />
              ) : heroImage ? (
                <ResponsiveImage // Desktop WebP
                  slug={heroImage}
                  metadata={heroImageMetaData}
                  aspectRatio={aspectRatio}
                  defaultWidth={defaultWidth}
                  widths={widths}
                  type="image/webp"
                  sizes="( max-width: 500px ) 98%, ( max-width: 875px ) 48%, 23%"
                  imgParams={{ auto: ["format", "compress"], fmt: "webp" }}
                  className="image"
                  alt={story["hero-image-caption"] || story.headline}
                  eager={eager}
                  loading={eager ? "eager" : "lazy"}
                />
              ) : showAlternateImage ? (
                <img
                  className="picture"
                  src={`${assetify(DefaultCardImage)}`}
                  alt={story["hero-image-caption"] || story.headline}
                  styleName="default-image"
                />
              ) : null
            }
          </WithLazy>
        </picture>

        {showStoryTypeIcon && (
          <span styleName="story-type-wrapper" className="story-type-wrapper">
            {story["story-template"] === "video" &&
              (darkTheme ? (
                <VideoBlackIcon largeIcon={largeIcon} />
              ) : (
                <Icon type="video-whitebg" className="video-whitebg" />
              ))}
            {story["story-template"] === "photo" &&
              (darkTheme ? (
                <GalleryBlackIcon largeIcon={largeIcon} />
              ) : (
                <Icon type="gallery-whitebg" className="gallery-whitebg" />
              ))}
          </span>
        )}
      </figure>
    </div>
  );
};

CardImage.propTypes = {
  story: object,
  defaultWidth: number,
  imageType: string,
  widths: array,
  zoom: bool,
  eager: bool,
  aspectRatio: array,
  showStoryTypeIcon: bool,
  showAlternateImage: bool,
  darkTheme: bool,
  largeIcon: bool,
  isCommentedStory: bool,
};

export default CardImage;
